import { useQuery } from "@tanstack/react-query";
import { api } from "../../../../singleton/axios";
import { useTourMode } from "../../../../contexts/TourModeContext";

export interface Quote {
  symbol: string;
  name: string;
  price: number;
  changePercentage: number;
  change: number;
  volume: number;
  dayLow: number;
  dayHigh: number;
  yearHigh: number;
  yearLow: number;
  marketCap: number;
  priceAvg50: number;
  priceAvg200: number;
  exchange: string;
  open: number;
  previousClose: number;
  timestamp: number;
}

export function useCompanyQuote(ticker: string) {
  const { isTourMode, getMockData } = useTourMode();

  return useQuery<Quote[] | Quote, Error>({
    queryKey: ["companyQuote", ticker, isTourMode],
    queryFn: async () => {
      // Return mock data in tour mode
      if (isTourMode) {
        const mockData = getMockData<Quote>("companyQuote");
        if (mockData) return mockData;
      }
      const { data } = await api.get<Quote[] | Quote>("/company-quote", {
        params: { ticker },
      });
      return data;
    },
    enabled: isTourMode || !!ticker,
    refetchOnWindowFocus: false,
    // Live prices every 10s. Cheap per tick because consumers derive a
    // value-stable `company` (identity only changes when the numbers do) and
    // the heavy chart children are memoized — a tick updates the price text,
    // not the whole tree.
    refetchInterval: isTourMode ? false : 10 * 1000,
    staleTime: isTourMode ? Infinity : 5 * 1000,
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
  });
}
